import { subscribeLearning, syncPaused } from './storage';
import { synchronizeAccount } from './sync';

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'offline' | 'retrying' | 'conflict' | 'paused';
export const syncMessages: Record<SyncStatus, string> = {
  paused: 'Po wyczyszczeniu danych lokalnych synchronizacja jest wstrzymana w tej przeglądarce. Możesz ją wznowić w ustawieniach danych lokalnych.',
  pending: 'Zmiany zapisane na urządzeniu. Za chwilę trafią do konta.',
  syncing: 'Zapisujemy postępy w koncie…',
  synced: 'Historia, postęp i ulubione są zsynchronizowane automatycznie.',
  offline: 'Brak połączenia. Postępy są zapisane na urządzeniu i trafią do konta po powrocie internetu.',
  retrying: 'Konto jest chwilowo niedostępne. Postępy są zapisane na urządzeniu; ponowimy zapis automatycznie.',
  conflict: 'Wykryto różne wersje egzaminu. Wybierz poniżej, jak je zachować.'
};
const statuses = new Map<string, SyncStatus>();
const listeners = new Set<() => void>();
export const accountSyncStatus = (userId: string) => statuses.get(userId) || 'pending';
export function subscribeSyncStatus(listener: () => void) {
  listeners.add(listener); return () => { listeners.delete(listener); };
}

// One queue for the signed-in account, across all routes. Local writes are already
// durable in IndexedDB before they schedule this queue; retries never discard them.
export function startAutomaticSync(userId: string, token: string) {
  let stopped = false; let inFlight = false; let dirty = false;
  let lastStarted = -Infinity; let failures = 0; let retryAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const publish = (status: SyncStatus) => {
    if (stopped) return;
    statuses.set(userId, status); listeners.forEach((listener) => listener());
  };
  const schedule = (delay = 0) => {
    if (syncPaused(userId)) { publish('paused'); return; }
    if (stopped || inFlight || timer !== undefined) return;
    if (!navigator.onLine) { publish('offline'); return; }
    timer = setTimeout(() => { timer = undefined; void run(); }, Math.max(delay, lastStarted + 3000 - Date.now(), retryAt - Date.now()));
  };
  const run = async () => {
    if (syncPaused(userId)) { publish('paused'); return; }
    if (stopped) return;
    if (!navigator.onLine) { publish('offline'); return; }
    inFlight = true; dirty = false; lastStarted = Date.now(); publish('syncing');
    try {
      const result = await synchronizeAccount(userId, token);
      if (stopped) return;
      failures = 0; retryAt = 0;
      publish(!navigator.onLine ? 'offline' : dirty ? 'pending' : result.conflicts ? 'conflict' : 'synced');
    } catch {
      if (stopped) return;
      if (syncPaused(userId)) { publish('paused'); return; }
      dirty = true; failures++;
      retryAt = Date.now() + Math.min(60000, 5000 * 2 ** Math.min(failures - 1, 4));
      publish(navigator.onLine ? 'retrying' : 'offline');
    } finally {
      inFlight = false;
      if (!stopped && dirty) schedule();
    }
  };
  const unsubscribe = subscribeLearning((change) => {
    if (change.userId === userId && change.source === 'cleared') {
      dirty = false; if (timer !== undefined) clearTimeout(timer); timer = undefined; publish('paused'); return;
    }
    if (change.userId !== userId || change.source !== 'local') return;
    dirty = true;
    if (!inFlight) publish(navigator.onLine ? 'pending' : 'offline');
    schedule(1000);
  });
  const reconnect = () => {
    if (timer !== undefined) clearTimeout(timer); timer = undefined;
    retryAt = 0; schedule();
  };
  const offline = () => { publish('offline'); };
  const visible = () => { if (document.visibilityState === 'visible') schedule(); };
  window.addEventListener('online', reconnect);
  window.addEventListener('offline', offline);
  window.addEventListener('focus', visible);
  document.addEventListener('visibilitychange', visible);
  // Refresh other devices' changes while the app is visible, without empty writes.
  const poll = setInterval(visible, 30000);
  publish(navigator.onLine ? 'pending' : 'offline');
  schedule();
  return () => {
    stopped = true; unsubscribe();
    if (timer !== undefined) clearTimeout(timer); clearInterval(poll);
    window.removeEventListener('online', reconnect);
    window.removeEventListener('offline', offline);
    window.removeEventListener('focus', visible);
    document.removeEventListener('visibilitychange', visible);
  };
}
